import { cx } from '@emotion/css';
import { BodySmallMidEmphasis, Tag, TitleSmallEmphasized } from '@/design-system';
import { APP_ROUTES, FlyerType } from '@/config/constants';
import { Category } from '@/graphql/types';
import { useNavigate } from 'react-router-dom';
import AuthenticationContext from '@/context/AuthenticationContext';
import { useContext } from 'react';
import { useCategories } from '@/hooks';
import { useReactiveVar } from '@apollo/client';
import { openLoginDrawerVar } from '@/graphql/state';
import { AccessibleDivButton, AccessibleList, AccessibleListItem } from '@/components';
import styles from './SimilarFlyers.styles';

interface Props {
  categories: string[];
  flyerType: string;
  disabled: boolean;
  isCommunityFree: boolean;
}

export const SimilarFlyers: React.FC<Props> = ({
  categories,
  flyerType,
  disabled,
  isCommunityFree,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthenticationContext);
  const openLoginDrawer = useReactiveVar(openLoginDrawerVar);
  const { getCategoriesByType } = useCategories();
  const categoriesIcons = getCategoriesByType(
    flyerType === FlyerType.school ? FlyerType.school : FlyerType.community
  );
  const mappedCategories = categoriesIcons.filter(category => categories.includes(category.id));

  const onClick = ({ id, title }: Pick<Category, 'id' | 'title'>) => {
    if (!isAuthenticated && !openLoginDrawer) {
      openLoginDrawerVar(true);
      return;
    }
    navigate(`/explore/all?categoryId=${id}&categoryName=${title}&tab=${flyerType}`);
  };

  const onClickTagFree = () => {
    navigate(APP_ROUTES.commFreeFlyerboard);
  };

  return (
    <div className={cx(styles.container, disabled && styles.disabled)}>
      <TitleSmallEmphasized as="h2">Similar Flyers</TitleSmallEmphasized>
      <AccessibleList className={styles.categoryList}>
        {mappedCategories.map(({ id, title, icon }) => (
          <AccessibleListItem key={id}>
            <AccessibleDivButton
              className={styles.card}
              onClick={() => onClick({ id, title })}
              role="link"
            >
              <img src={icon} width="18px" height="18px" alt="" className={styles.icon} />
              <BodySmallMidEmphasis as="span" className={styles.text} data-testid="categoryName">
                {title}
              </BodySmallMidEmphasis>
            </AccessibleDivButton>
          </AccessibleListItem>
        ))}
        {isCommunityFree && (
          <AccessibleListItem>
            <Tag variant="free" onClick={onClickTagFree} className={styles.free}>
              Free
            </Tag>
          </AccessibleListItem>
        )}
      </AccessibleList>
    </div>
  );
};
